import { useEffect, useRef } from "react";

import { useGlobalPayments } from "Payments/GlobalPaymentsContext";

import { usePaymentMethodState } from "./";
import API from "./API";

export default function SavePaymentMethod({ setInputErrors }) {
  const saveComplete = useRef(false);

  const {
    setState,
    submitting,
    ccReadyToSubmit,
    additionalInfoReadyToSubmit,
    onSuccess,
    mustSaveCard,
    canSaveCard,
    replaceExistingCard,
    //post data
    userID,
    paymentMethodToken,
    name,
    zip,
    last4,
    month,
    year,
    cardType,
    isDefaultPaymentMethod,
    isTextToGiveEnabled,
    // isEnabledForTTG,
    isSharedWithHousehold,
    // sharedWithOtherHoH,
    isSavedPaymentMethod,
    nickname,
    showingNicknameCheckbox,
    paymentMethodID,
    email,
    phone,
    tabID,
    ach,
  } = usePaymentMethodState();

  const { state } = useGlobalPayments();
  const { paymentMethodProviders } = state;

  const cardIsReady =
    submitting && ccReadyToSubmit && additionalInfoReadyToSubmit;
  const bankIsReady =
    (cardType === "checking" || cardType === "savings") &&
    ((submitting &&
      !ach.isEditing &&
      paymentMethodID &&
      !replaceExistingCard) ||
      (submitting && ach.isEditing && paymentMethodToken?.length > 0));

  const determineIfBankAccount = isBankAccount(cardType);
  const determineIfCreditCard = isCreditCard(cardType);

  let readyToSave;
  if (determineIfBankAccount) {
    readyToSave = bankIsReady;
  } else if (determineIfCreditCard) {
    readyToSave = cardIsReady;
  } else {
    readyToSave = false;
  }

  useEffect(() => {
    if (!readyToSave || saveComplete.current) return;

    let postData = getPostData({
      paymentMethodToken,
      cardType,
      name,
      zip,
      last4,
      month,
      year,
      isDefaultPaymentMethod,
      isTextToGiveEnabled,
      isSharedWithHousehold,
      isSavedPaymentMethod,
      userID,
      nickname,
      showingNicknameCheckbox,
      canSaveCard,
      mustSaveCard,
      replaceExistingCard,
      email,
      phone,
      tabID,
      ach,
      paymentMethodProviders,
    });

    if (paymentMethodID) postData.paymentMethodID = paymentMethodID;

    const handleError = (error) => {
      // console.log({ error });
      let msg;
      switch (error) {
        case "INVALID_POSTAL_CODE_FOR_COUNTRY":
          msg = "invalid_zip";
          break;
        case "PARAM_VALUE_IS_TOO_LONG":
          msg = "name_too_long";
          break;
        default:
          msg = "default";
          break;
      }
      setInputErrors((prev) => {
        const newErrors = Array.from(new Set([...prev, ...[msg]])); // removes duplicate error messages
        return newErrors;
      });
    };

    // console.log({ postData });

    let saveCall;
    if (
      replaceExistingCard
      // && !determineIfBankAccount
    ) {
      saveCall = API.replaceSavedPaymentMethod(postData);
    } else if (paymentMethodID) {
      saveCall = API.updateSavedPaymentMethod(postData);
    } else {
      saveCall = API.createPaymentMethod(postData);
    }

    saveCall
      .post()
      .then((resp) => {
        // console.log(resp);
        setState({
          submitting: false,
          ccReadyToSubmit: false,
          additionalInfoReadyToSubmit: true,
          replaceExistingCard: false,
        });
        saveComplete.current = true;
        if (onSuccess) onSuccess(resp);
      })
      .catch((err) => {
        // console.log(err);
        setState({
          submitting: false,
          ccReadyToSubmit: false,
          additionalInfoReadyToSubmit: true,
        });
        handleError(err.message);
      });

    return () => {
      saveCall.cancel();
    };
  }, [
    readyToSave,
    paymentMethodToken,
    name,
    zip,
    last4,
    month,
    year,
    cardType,
    isDefaultPaymentMethod,
    isTextToGiveEnabled,
    isSharedWithHousehold,
    userID,
    nickname,
    replaceExistingCard,
    paymentMethodID,
    showingNicknameCheckbox,
    setInputErrors,
    ach,
    canSaveCard,
    email,
    isSavedPaymentMethod,
    mustSaveCard,
    paymentMethodProviders,
    phone,
    tabID,
  ]);

  return null;
}

const getPostData = ({
  paymentMethodToken,
  cardType,
  name,
  zip,
  last4,
  month,
  year,
  isDefaultPaymentMethod,
  isTextToGiveEnabled,
  isSharedWithHousehold,
  isSavedPaymentMethod,
  userID,
  nickname,
  showingNicknameCheckbox,
  canSaveCard,
  mustSaveCard,
  replaceExistingCard,
  email,
  phone,
  tabID,
  ach,
  paymentMethodProviders,
}) => {
  const isCard =
    cardType === "visa" ||
    cardType === "mastercard" ||
    cardType === "amex" ||
    cardType === "discover";
  const isBank = cardType === "checking" || cardType === "savings";

  let postData = {
    creditCardToken: isCard ? paymentMethodToken : null,
    bankAccountToken: isBank ? paymentMethodToken : null,
    cardHolderName: name,
    billingAddress: { zip },
    last4: last4,
    expirationMonth: isCard ? month : null,
    expirationYear: isCard ? year : null,
    cardType: cardType,
    accountType: isBank ? ach.accountType : null,
    accountHolderType: isBank ? ach.accountHolderType : null,
    isDefaultPaymentMethod,
    isEnabledForTTG: isTextToGiveEnabled,
    sharedWithOtherHoH: isSharedWithHousehold,
    userID,
    nickname:
      showingNicknameCheckbox || showingNicknameCheckbox === null
        ? nickname
        : "",
    save:
      canSaveCard &&
      (mustSaveCard || isSavedPaymentMethod || replaceExistingCard),
    email,
    phone,
    tabID,
    provider: getProvider(paymentMethodProviders, cardType),
  };
  return postData;
};

const getProvider = (paymentMethodProviders, cardType) => {
  const card = paymentMethodProviders.find(
    (method) => method.paymentMethodCategory === "credit_card"
  );
  const bank = paymentMethodProviders.find(
    (method) => method.paymentMethodCategory === "bank_account"
  );

  const provider =
    cardType === "checking" ||
    cardType === "savings" ||
    cardType === "ach" || // I'm not sure this is actually a possible value
    cardType === "bank" // I'm not sure this is actually a possible value
      ? bank.paymentProvider
      : card.paymentProvider;
  return getProviderName(provider);
};

const getProviderName = (paymentProvider) => {
  if (typeof paymentProvider === "number") {
    return paymentProvider === 1 ? "wepay" : "stax";
  }

  return paymentProvider;
};

const isBankAccount = (cardType) => {
  return cardType === "checking" || cardType === "savings";
};

const isCreditCard = (cardType) => {
  if (!cardType || typeof cardType !== "string") {
    return false;
  }
  switch (cardType.toLowerCase()) {
    case "amex":
    case "visa":
    case "discover":
    case "mastercard":
      return true;
    default:
      return false;
  }
};
